/**
 * usePoolLiquidity Hook
 *
 * React hook for fetching and displaying pool liquidity data with:
 * - Independent loading per pool
 * - Automatic retry for failed pools
 * - Refresh/invalidation support
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  PoolLiquidityData,
  PoolCacheEntry,
  PoolId,
  getAllPoolIds,
  getPoolCacheKey,
  createInitialPoolEntry,
  shouldRetryPool,
  fetchPoolCacheEntry,
} from '@/services/poolLiquidityCacheService'
import { isPageVisible } from '@/lib/pageVisibility'

// Retry check interval
const RETRY_CHECK_INTERVAL = 1000

export interface PoolWithStatus {
  poolId: PoolId
  cacheKey: string
  entry: PoolCacheEntry
}

export interface UsePoolLiquidityResult {
  pools: PoolLiquidityData[]
  poolsWithStatus: PoolWithStatus[]
  loading: boolean
  failedCount: number
  refetch: () => void
}

/**
 * Fetch liquidity data for all pools with granular caching
 */
export function usePoolLiquidity(): UsePoolLiquidityResult {
  const [cache, setCache] = useState<Map<string, PoolCacheEntry>>(() => {
    const initialCache = new Map<string, PoolCacheEntry>()
    for (const poolId of getAllPoolIds()) {
      initialCache.set(getPoolCacheKey(poolId), createInitialPoolEntry())
    }
    return initialCache
  })

  const retryTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const fetchingRef = useRef<Set<string>>(new Set())
  const poolIdsRef = useRef<PoolId[]>(getAllPoolIds())

  // Update a specific cache entry
  const updateEntry = useCallback((cacheKey: string, entry: PoolCacheEntry) => {
    setCache(prev => {
      const next = new Map(prev)
      next.set(cacheKey, entry)
      return next
    })
    // Remove from fetching set when done
    if (entry.status !== 'loading') {
      fetchingRef.current.delete(cacheKey)
    }
  }, [])

  // Fetch a specific pool
  const fetchPool = useCallback((poolId: PoolId) => {
    const cacheKey = getPoolCacheKey(poolId)

    // Skip if already fetching
    if (fetchingRef.current.has(cacheKey)) return
    fetchingRef.current.add(cacheKey)

    const currentEntry = cache.get(cacheKey) || createInitialPoolEntry()

    fetchPoolCacheEntry(
      poolId,
      currentEntry,
      (entry) => updateEntry(cacheKey, entry)
    )
  }, [cache, updateEntry])

  // Fetch all pools that are ready (idle or should retry)
  const fetchReadyPools = useCallback(() => {
    for (const poolId of poolIdsRef.current) {
      const cacheKey = getPoolCacheKey(poolId)
      const entry = cache.get(cacheKey)

      if (!entry || entry.status === 'idle') {
        fetchPool(poolId)
      } else if (entry.status === 'failed' && shouldRetryPool(entry)) {
        fetchPool(poolId)
      }
    }
  }, [cache, fetchPool])

  // Initial fetch on mount
  useEffect(() => {
    fetchReadyPools()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Setup retry timer
  useEffect(() => {
    retryTimerRef.current = setInterval(() => {
      if (!isPageVisible()) return
      for (const poolId of poolIdsRef.current) {
        const cacheKey = getPoolCacheKey(poolId)
        const entry = cache.get(cacheKey)

        if (entry && entry.status === 'failed' && shouldRetryPool(entry)) {
          fetchPool(poolId)
        }
      }
    }, RETRY_CHECK_INTERVAL)

    return () => {
      if (retryTimerRef.current) {
        clearInterval(retryTimerRef.current)
      }
    }
  }, [cache, fetchPool])

  // Refresh function - invalidates cache and refetches
  const refetch = useCallback(() => {
    // Clear fetching set
    fetchingRef.current.clear()

    // Reset all entries to idle
    const freshCache = new Map<string, PoolCacheEntry>()
    for (const poolId of poolIdsRef.current) {
      freshCache.set(getPoolCacheKey(poolId), createInitialPoolEntry())
    }
    setCache(freshCache)

    // Trigger fetches (will happen via effect)
  }, [])

  // Trigger fetches when cache resets to idle
  useEffect(() => {
    const allIdle = Array.from(cache.values()).every(e => e.status === 'idle')
    if (allIdle && cache.size > 0) {
      fetchReadyPools()
    }
  }, [cache, fetchReadyPools])

  // Build pools with status for granular rendering
  const poolsWithStatus: PoolWithStatus[] = poolIdsRef.current.map(poolId => {
    const cacheKey = getPoolCacheKey(poolId)
    const entry = cache.get(cacheKey) || createInitialPoolEntry()
    return { poolId, cacheKey, entry }
  })

  // Extract loaded pools for backwards compatibility
  const pools: PoolLiquidityData[] = poolsWithStatus
    .filter(p => p.entry.data !== null)
    .map(p => p.entry.data!)

  // Calculate derived state
  const loading = poolsWithStatus.some(p => p.entry.status === 'loading')
  const failedCount = poolsWithStatus.filter(p => p.entry.status === 'failed').length

  return {
    pools,
    poolsWithStatus,
    loading,
    failedCount,
    refetch,
  }
}

/**
 * Format USD value for display
 */
export function formatUsdValue(value: number | null): string {
  if (value === null) return '$—'
  if (value === 0) return '$0.00'
  if (value < 0.01) return '<$0.01'
  if (value < 1000) {
    return `$${value.toFixed(2)}`
  }
  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * Format token amount for display
 */
export function formatTokenAmount(amount: string, maxDecimals: number = 4): string {
  const num = parseFloat(amount)
  if (isNaN(num)) return '0'
  if (num === 0) return '0'
  if (num < 0.0001) return '<0.0001'
  if (num < 1) return num.toFixed(maxDecimals).replace(/\.?0+$/, '')
  if (num < 1000) return num.toFixed(maxDecimals).replace(/\.?0+$/, '')
  if (num < 1000000) {
    return `${(num / 1000).toFixed(2)}K`
  }
  if (num < 1000000000) {
    return `${(num / 1000000).toFixed(2)}M`
  }
  return `${(num / 1000000000).toFixed(2)}B`
}

/**
 * Truncate address for display
 */
export function truncateAddress(address: string): string {
  if (!address) return ''
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}
